/**
 * CAT (Comunicacao de Acidente de Trabalho) Types
 *
 * Type definitions for the CAT module.
 *
 * @version 2025.1.0
 */

// ========== ENUMS ==========

export enum CatReportType {
  INITIAL = 'INITIAL',
  REOPENING = 'REOPENING',
  DEATH_NOTICE = 'DEATH_NOTICE'
}

export enum CatStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  PROCESSED = 'PROCESSED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED'
}

export enum AccidentType {
  TYPICAL = 'TYPICAL',
  COMMUTING = 'COMMUTING',
  OCCUPATIONAL_DISEASE = 'OCCUPATIONAL_DISEASE'
}

export enum AccidentLocationType {
  EMPLOYER_ESTABLISHMENT = 'EMPLOYER_ESTABLISHMENT',
  THIRD_PARTY_ESTABLISHMENT = 'THIRD_PARTY_ESTABLISHMENT',
  PUBLIC_ROAD = 'PUBLIC_ROAD',
  HOME = 'HOME',
  OTHER = 'OTHER'
}

export enum CatIssuer {
  EMPLOYER = 'EMPLOYER',
  UNION = 'UNION',
  DOCTOR = 'DOCTOR',
  PUBLIC_AUTHORITY = 'PUBLIC_AUTHORITY',
  DEPENDENT = 'DEPENDENT'
}

export enum AffectedBodyPart {
  HEAD = 'HEAD',
  NECK = 'NECK',
  EYES = 'EYES',
  CHEST = 'CHEST',
  ABDOMEN = 'ABDOMEN',
  UPPER_LIMBS = 'UPPER_LIMBS',
  LOWER_LIMBS = 'LOWER_LIMBS',
  HANDS = 'HANDS',
  FEET = 'FEET',
  FINGERS = 'FINGERS',
  SPINE = 'SPINE',
  MULTIPLE = 'MULTIPLE',
  WHOLE_BODY = 'WHOLE_BODY',
  OTHER = 'OTHER'
}

export enum InjuryNature {
  FRACTURE = 'FRACTURE',
  DISLOCATION = 'DISLOCATION',
  SPRAIN = 'SPRAIN',
  CUT = 'CUT',
  LACERATION = 'LACERATION',
  CONTUSION = 'CONTUSION',
  BURN = 'BURN',
  AMPUTATION = 'AMPUTATION',
  POISONING = 'POISONING',
  ASPHYXIATION = 'ASPHYXIATION',
  FOREIGN_BODY = 'FOREIGN_BODY',
  OTHER = 'OTHER'
}

export enum Laterality {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  BOTH = 'BOTH'
}

// ========== LABELS ==========

export const CAT_REPORT_TYPE_LABELS: Record<CatReportType, string> = {
  [CatReportType.INITIAL]: 'Inicial',
  [CatReportType.REOPENING]: 'Reabertura',
  [CatReportType.DEATH_NOTICE]: 'Comunicação de Óbito'
};

export const CAT_STATUS_LABELS: Record<CatStatus, string> = {
  [CatStatus.DRAFT]: 'Rascunho',
  [CatStatus.SUBMITTED]: 'Enviada',
  [CatStatus.PROCESSED]: 'Processada',
  [CatStatus.REJECTED]: 'Rejeitada',
  [CatStatus.CANCELLED]: 'Cancelada'
};

export const CAT_STATUS_COLORS: Record<CatStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [CatStatus.DRAFT]: 'default',
  [CatStatus.SUBMITTED]: 'info',
  [CatStatus.PROCESSED]: 'success',
  [CatStatus.REJECTED]: 'error',
  [CatStatus.CANCELLED]: 'warning'
};

export const ACCIDENT_TYPE_LABELS: Record<AccidentType, string> = {
  [AccidentType.TYPICAL]: 'Típico',
  [AccidentType.COMMUTING]: 'Trajeto',
  [AccidentType.OCCUPATIONAL_DISEASE]: 'Doença Ocupacional'
};

export const ACCIDENT_TYPE_COLORS: Record<AccidentType, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [AccidentType.TYPICAL]: 'error',
  [AccidentType.COMMUTING]: 'warning',
  [AccidentType.OCCUPATIONAL_DISEASE]: 'info'
};

export const ACCIDENT_LOCATION_TYPE_LABELS: Record<AccidentLocationType, string> = {
  [AccidentLocationType.EMPLOYER_ESTABLISHMENT]: 'Estabelecimento do Empregador',
  [AccidentLocationType.THIRD_PARTY_ESTABLISHMENT]: 'Estabelecimento de Terceiros',
  [AccidentLocationType.PUBLIC_ROAD]: 'Via Pública',
  [AccidentLocationType.HOME]: 'Residência',
  [AccidentLocationType.OTHER]: 'Outro'
};

export const CAT_ISSUER_LABELS: Record<CatIssuer, string> = {
  [CatIssuer.EMPLOYER]: 'Empregador',
  [CatIssuer.UNION]: 'Sindicato',
  [CatIssuer.DOCTOR]: 'Médico',
  [CatIssuer.PUBLIC_AUTHORITY]: 'Autoridade Pública',
  [CatIssuer.DEPENDENT]: 'Dependente'
};

export const AFFECTED_BODY_PART_LABELS: Record<AffectedBodyPart, string> = {
  [AffectedBodyPart.HEAD]: 'Cabeça',
  [AffectedBodyPart.NECK]: 'Pescoço',
  [AffectedBodyPart.EYES]: 'Olhos',
  [AffectedBodyPart.CHEST]: 'Tórax',
  [AffectedBodyPart.ABDOMEN]: 'Abdômen',
  [AffectedBodyPart.UPPER_LIMBS]: 'Membros Superiores',
  [AffectedBodyPart.LOWER_LIMBS]: 'Membros Inferiores',
  [AffectedBodyPart.HANDS]: 'Mãos',
  [AffectedBodyPart.FEET]: 'Pés',
  [AffectedBodyPart.FINGERS]: 'Dedos',
  [AffectedBodyPart.SPINE]: 'Coluna',
  [AffectedBodyPart.MULTIPLE]: 'Múltiplas',
  [AffectedBodyPart.WHOLE_BODY]: 'Corpo Inteiro',
  [AffectedBodyPart.OTHER]: 'Outro'
};

export const INJURY_NATURE_LABELS: Record<InjuryNature, string> = {
  [InjuryNature.FRACTURE]: 'Fratura',
  [InjuryNature.DISLOCATION]: 'Luxação',
  [InjuryNature.SPRAIN]: 'Entorse',
  [InjuryNature.CUT]: 'Corte',
  [InjuryNature.LACERATION]: 'Laceração',
  [InjuryNature.CONTUSION]: 'Contusão',
  [InjuryNature.BURN]: 'Queimadura',
  [InjuryNature.AMPUTATION]: 'Amputação',
  [InjuryNature.POISONING]: 'Intoxicação',
  [InjuryNature.ASPHYXIATION]: 'Asfixia',
  [InjuryNature.FOREIGN_BODY]: 'Corpo Estranho',
  [InjuryNature.OTHER]: 'Outro'
};

export const LATERALITY_LABELS: Record<Laterality, string> = {
  [Laterality.LEFT]: 'Esquerdo',
  [Laterality.RIGHT]: 'Direito',
  [Laterality.BOTH]: 'Ambos'
};

// ========== MAIN INTERFACES ==========

export interface Cat {
  id: number;
  employeeId: number;
  companyId: number;

  // CAT Data
  reportType: CatReportType;
  accidentType: AccidentType;
  accidentDate: string;
  accidentTime?: string;
  hoursWorkedBefore?: string;
  generatingSituation: string;
  issuer: CatIssuer;
  isDeathReport: string;

  // Accident Location
  accidentLocationType: AccidentLocationType;
  streetType?: string;
  streetName?: string;
  streetNumber?: string;
  complement?: string;
  neighborhood?: string;
  zipCode?: string;
  cityCode?: string;
  state?: string;
  countryCode?: string;

  // Injury and Diagnosis
  affectedBodyPart: AffectedBodyPart;
  laterality?: Laterality;
  injuryNature: InjuryNature;
  injuryDescription?: string;
  causingAgent: string;
  probableDiagnosis?: string;
  cidCodeId?: number;

  // Medical Treatment
  treatmentDate?: string;
  treatmentTime?: string;
  wasHospitalized?: string;
  treatmentDuration?: number;
  willBeAbsent?: string;

  // Issuer Professional Information
  issuerName?: string;
  classEntity?: string;
  classEntityNumber?: string;
  classEntityState?: string;

  // eSocial Integration
  sendToEsocial: boolean;
  esocialEnvironment?: string;
  esocialEventId?: string;
  esocialStatus?: string;
  codParteAting?: string;
  codAgntCausador?: string;
  codNatLesao?: string;
  codSitGeradora?: string;

  // Controls
  issueDate?: string;
  status: CatStatus;
  notes?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;

  // Relations
  employee?: {
    id: number;
    name: string;
    cpf: string;
    admissionDate?: string;
  };
  company?: {
    id: number;
    tradeName: string;
    legalName: string;
    cnpj: string;
  };
  cidCode?: {
    id: number;
    code: string;
    description: string;
  };
}

// ========== FORM DATA ==========

export interface CatFormData {
  employeeId: number | '';
  companyId: number | '';
  reportType: CatReportType | '';
  accidentType: AccidentType | '';
  accidentDate: string;
  accidentTime?: string;
  hoursWorkedBefore?: string;
  generatingSituation: string;
  issuer: CatIssuer | '';
  isDeathReport: string;
  accidentLocationType: AccidentLocationType | '';
  streetType?: string;
  streetName?: string;
  streetNumber?: string;
  complement?: string;
  neighborhood?: string;
  zipCode?: string;
  cityCode?: string;
  state?: string;
  countryCode?: string;
  affectedBodyPart: AffectedBodyPart | '';
  laterality?: Laterality | '';
  injuryNature: InjuryNature | '';
  injuryDescription?: string;
  causingAgent: string;
  probableDiagnosis?: string;
  cidCodeId?: number | '';
  treatmentDate?: string;
  treatmentTime?: string;
  wasHospitalized?: string;
  treatmentDuration?: number | '';
  willBeAbsent?: string;
  issuerName?: string;
  classEntity?: string;
  classEntityNumber?: string;
  classEntityState?: string;
  sendToEsocial: boolean;
  esocialEnvironment?: string;
  notes?: string;
  // eSocial reference table codes (populated via ESocialReferenceAutocomplete)
  codParteAting?: string;
  codAgntCausador?: string;
  codNatLesao?: string;
  codSitGeradora?: string;
}

// ========== REQUEST/RESPONSE ==========

export interface CreateCatRequest {
  employeeId: number;
  companyId: number;
  reportType: CatReportType;
  accidentType: AccidentType;
  accidentDate: string;
  accidentTime?: string;
  hoursWorkedBefore?: string;
  generatingSituation: string;
  issuer: CatIssuer;
  isDeathReport?: string;
  accidentLocationType: AccidentLocationType;
  streetType?: string;
  streetName?: string;
  streetNumber?: string;
  complement?: string;
  neighborhood?: string;
  zipCode?: string;
  cityCode?: string;
  state?: string;
  countryCode?: string;
  affectedBodyPart: AffectedBodyPart;
  laterality?: Laterality;
  injuryNature: InjuryNature;
  injuryDescription?: string;
  causingAgent: string;
  probableDiagnosis?: string;
  cidCodeId?: number;
  treatmentDate?: string;
  treatmentTime?: string;
  wasHospitalized?: string;
  treatmentDuration?: number;
  willBeAbsent?: string;
  issuerName?: string;
  classEntity?: string;
  classEntityNumber?: string;
  classEntityState?: string;
  sendToEsocial?: boolean;
  esocialEnvironment?: string;
  notes?: string;
  codParteAting?: string;
  codAgntCausador?: string;
  codNatLesao?: string;
  codSitGeradora?: string;
}

export interface UpdateCatRequest extends Partial<CreateCatRequest> {}

export interface CatFilters {
  search?: string;
  companyId?: number;
  employeeId?: number;
  status?: CatStatus[];
  reportType?: CatReportType;
  accidentType?: AccidentType;
  startDate?: string;
  endDate?: string;
}

export interface CatListResponse {
  data: Cat[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CatStats {
  total: number;
  byStatus: Record<string, number>;
  byAccidentType: Record<string, number>;
  byReportType: Record<string, number>;
  overdue: number;
  thisMonth: number;
  thisYear: number;
}

// ========== UTILITY FUNCTIONS ==========

export const getCatStatusColor = (status: CatStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  return CAT_STATUS_COLORS[status] || 'default';
};

export const getCatStatusLabel = (status: CatStatus): string => {
  return CAT_STATUS_LABELS[status] || status;
};

export const getAccidentTypeColor = (type: AccidentType): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  return ACCIDENT_TYPE_COLORS[type] || 'default';
};

export const getAccidentTypeLabel = (type: AccidentType): string => {
  return ACCIDENT_TYPE_LABELS[type] || type;
};

export const getReportTypeLabel = (type: CatReportType): string => {
  return CAT_REPORT_TYPE_LABELS[type] || type;
};

export const canCatBeEdited = (status: CatStatus): boolean => {
  return status === CatStatus.DRAFT;
};

export const canCatBeCancelled = (status: CatStatus): boolean => {
  return status !== CatStatus.PROCESSED && status !== CatStatus.CANCELLED;
};

export const canCatBeSubmitted = (status: CatStatus): boolean => {
  return status === CatStatus.DRAFT;
};

export const formatCatDate = (date: string): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('pt-BR');
};

export const formatCatDateTime = (date: string, time?: string): string => {
  if (!date) return '';
  const dateStr = formatCatDate(date);
  if (time) {
    return `${dateStr} às ${time.substring(0, 5)}`;
  }
  return dateStr;
};

/**
 * Get default form data for a new CAT
 */
export const getDefaultCatFormData = (): CatFormData => ({
  employeeId: '',
  companyId: '',
  reportType: CatReportType.INITIAL,
  accidentType: '',
  accidentDate: '',
  accidentTime: '',
  hoursWorkedBefore: '',
  generatingSituation: '',
  issuer: CatIssuer.EMPLOYER,
  isDeathReport: 'N',
  accidentLocationType: '',
  streetType: '',
  streetName: '',
  streetNumber: '',
  complement: '',
  neighborhood: '',
  zipCode: '',
  cityCode: '',
  state: '',
  countryCode: '1058',
  affectedBodyPart: '',
  laterality: '',
  injuryNature: '',
  injuryDescription: '',
  causingAgent: '',
  probableDiagnosis: '',
  cidCodeId: '',
  treatmentDate: '',
  treatmentTime: '',
  wasHospitalized: 'N',
  treatmentDuration: '',
  willBeAbsent: 'N',
  issuerName: '',
  classEntity: '',
  classEntityNumber: '',
  classEntityState: '',
  sendToEsocial: false,
  esocialEnvironment: '',
  notes: ''
});

/** Marcações de ponto do dia do acidente relatado na CAT, para conferência da jornada. */
export interface MarcacaoDoDiaCat {
  punchAt: string;
  punchType: string;
}

export interface PontoDoDiaCat {
  data: string;
  marcacoes: MarcacaoDoDiaCat[];
  accidentTime: string | null;
}
